import { MAX_COMBINED_NUMBER_OF_SAMPLES } from 'custom_hooks/data_hooks/v4_sensor_settings/shared';

export const V4_SETTINGS_TEXT = {
    sections: {
        collectionSettings: 'Collection Settings',
        advancedSettings: 'Advanced Settings',
    },

    settingNames: {
        vibrationX: 'Vibration Data (X Axis)',
        vibrationY: 'Vibration Data (Y Axis)',
        vibrationZ: 'Vibration Data (Z Axis)',
        envelopedData: 'Enveloped Data (Y Axis)',
        ultrasoundEnveloped: 'Ultrasound Enveloped',
        measuredSpeed: 'Measured Speed (Magnetic Field)',
        samplingFrequency: 'Sampling Frequency',
        numberOfSamples: 'Number of Samples',
        axesEnabled: 'Axes Enabled',
        updateInterval: 'Update Interval',
        lowFrequencyCollection: 'Low-Frequency Collection',
        collectionOffset: 'Collection Offset',
        collectionOffsetForward: 'Collection Offset (Forward)',
        minTimeBetweenTriggers: 'Minimum Time Between Triggers',
        threshold: 'Threshold (0.1-25 g)',
    },

    descriptions: {
        advancedSettingsDescription:
            'These settings allow you to collect vibration data for X, Y, and Z axes or enveloped data for the Y-axis. Battery consumption increases as more settings are enabled.',
        collectionSettingsSensorDescription:
            'These are the basic settings that define data collection for this sensor.',
        collectionSettingsLocationDescription:
            'These are the basic settings that define data collection for V4 sensors at this location.',
        envelopedDataHelper:
            'Enveloping highlights repetitive impacts in the vibration signal, making it easier to detect developing bearing faults, especially on slow-moving equipment.',
        ultrasoundHelper:
            'High-frequency acoustic data will be recorded for early fault detection.',
        measuredSpeedHelper:
            'Estimates motor turning speed automatically. This is enabled by default for sensors assigned to Motor assets.',
    },

    disabledHints: {
        allAxesDisabledHint:
            'Enable vibration data for X, Y, or Z axis in Advanced Settings to sample data',
        envelopedDisabledHint: 'Enable enveloped data for Y axis to sample data',
        vibrationYDisabledHint: 'Enable vibration data for Y axis to sample data',
    },

    lowFrequencyCollection: {
        primaryCapExhaustedWarning:
            'Low-frequency collection is disabled until number of samples is reduced',
        enabledHelper: `Enables a separate, lower-frequency collection. Useful for detecting slow-speed faults without impacting the primary collection. Must be lower than primary collection, and combined with it, cannot exceed ${MAX_COMBINED_NUMBER_OF_SAMPLES} samples.`,
        readOnlyHelper:
            'Enables a separate, lower-frequency collection. Useful for detecting slow-speed faults without impacting the primary collection. Must be lower than primary collection.',
        noValidConfigHelper:
            'No Low-Frequency Collection configuration is available at the current primary collection settings. Lower the primary collection frequency or number of samples, or turn this off.',
        exceedsLimitsHelper:
            'This combination exceeds the primary collection limits. Adjust the sampling frequency or number of samples below to save.',
        mustBeLowerFrequency: 'Must be lower than the primary collection frequency.',
        combinedPrimaryExceeds: `Combined with primary collection, this would exceed ${MAX_COMBINED_NUMBER_OF_SAMPLES} samples.`,
        combinedLfcExceeds: `Combined with Low-Frequency Collection, this would exceed ${MAX_COMBINED_NUMBER_OF_SAMPLES} samples.`,
        mustBeHigherFrequency:
            'Must be higher than the Low-Frequency Collection frequency.',
        nowDisabledWarning: 'Low-Frequency Collection is now disabled',
    },

    updateInterval: {
        smartsampleHelper:
            'Automatically adjusts how often it collects based on machine health.',
        smartsampleBulletBattery:
            'Saves battery when the asset is stable, collects more data when behavior changes.',
        smartsampleBulletBaseline:
            'Takes 7 days to fill the baseline and uses a default update interval of 10 minutes until then.',
        triggeredHelperLine1:
            'Collects only when vibration exceeds a set threshold on Y axis.',
        triggeredHelperLine2:
            'Best for capturing intermittent events and focusing on specific operating conditions.',
        timedHelper:
            'Collects at a fixed interval (e.g., every hour). Best for steady monitoring and consistent trends.',
        thresholdValidationError: 'Enter a threshold between 0.1 and 25 g',
        collectionOffsetValidationError:
            'Enter a collection offset between 0 and 65,000 ms',
    },

    axisWarnings: {
        updateIntervalChangedWarning: (fromLabel: string) =>
            `Update Interval changed from ${fromLabel} to Timed`,
        envelopedNowDisabled: 'Enveloped Data is now disabled',
    },

    validation: {
        collectionDurationExceedsUpdateInterval:
            'Collection duration is greater than update interval. Please adjust to save.',
        collectionDurationExceedsThisInterval:
            'Collection duration is greater than this interval. Please adjust to save.',
    },

    modal: {
        toastSaveSuccess: 'Saved sensor settings',
        toastSaveError: 'Failed to save V4 sensor settings.',
        confirmStopEditing:
            'Are you sure you want to stop editing? Your work will not be saved.',
        confirmStopEditingLabel: 'Stop Editing',
        titleEditSettings: 'Edit Settings',
        errorLoadSettings: 'Unable to load V4 sensor settings.',
    },

    page: {
        sensorCalloutText: (locationName: string) =>
            `Changes affect only this sensor. Settings will remain unchanged for other V4 sensors at [${locationName}].`,
        locationCalloutText: (locationName: string) =>
            `Changes apply to V4 Sensors at [${locationName}] but will not override individually configured V4 sensors`,
        documentTitle: (locationName: string) => `${locationName} V4 Sensor Settings`,
        title: (locationName: string) => `[${locationName}] V4 Sensor Settings`,
    },
} as const;
